import { LoginState } from "@propelauth/frontend-apis";

/**
 * What a login state means for this screen.
 *
 * PropelAuth answers a login or a signup with one of eight states, and only
 * one of them means the session is live. The rest are steps somebody still has
 * to take.
 *
 * Nothing here navigates on its own any more. The screen used to redirect to
 * the hosted pages after a second and a half for every state it did not
 * implement, which meant somebody who had just typed a password into a page in
 * this application's type was moved, without being asked, to a page in
 * somebody else's — carrying, as it turned out, PropelAuth's unfilled
 * placeholder copy. A step this application cannot complete is still worth
 * saying in its own words, with a button rather than a timer.
 */
export type NextStep =
  /** The session is live. */
  | { kind: "signedIn" }
  /** Ours, and the common one: every new account passes through it. */
  | { kind: "confirmEmail" }
  /** The credentials did not produce a session, so the form is the answer. */
  | { kind: "retry"; message: string }
  /**
   * A step with no screen here. Explained in our own words and entered by a
   * button — never by a timer, and never without saying where it goes.
   */
  | { kind: "continueElsewhere"; title: string; explanation: string };

export function nextStepFor(loginState: string): NextStep {
  switch (loginState) {
    case LoginState.LOGGED_IN:
      return { kind: "signedIn" };

    case LoginState.EMAIL_NOT_CONFIRMED_YET:
      return { kind: "confirmEmail" };

    case LoginState.LOGIN_REQUIRED:
      /**
       * The credentials produced no session, so the form is the answer —
       * leaving for the hosted login page to be asked for a password again is
       * a round trip to arrive where we already are.
       *
       * The wording is careful because this state is not only wrong passwords.
       * A signup that sent a confirmation email answered `LoginRequired` too,
       * and telling somebody whose account had just been made that their
       * details were wrong is worse than saying nothing. The caller settles
       * the state against `fetchLoginState()` before reaching here, so that
       * particular case no longer arrives — and if a new one does, this says
       * what happened rather than accusing them of mistyping.
       */
      return {
        kind: "retry",
        message: "That did not complete the sign-in. Try again, and check the email and password if it repeats.",
      };

    case LoginState.TWO_FACTOR_REQUIRED:
      return {
        kind: "continueElsewhere",
        title: "Two-step verification",
        explanation: "This account asks for a code as well as a password. Entering it is one more page.",
      };

    case LoginState.TWO_FACTOR_ENROLLMENT_REQUIRED:
      return {
        kind: "continueElsewhere",
        title: "Set up two-step verification",
        explanation: "Your workspace requires it before you can sign in. Setting it up takes a minute.",
      };

    case LoginState.UPDATE_PASSWORD_REQUIRED:
      return {
        kind: "continueElsewhere",
        title: "Choose a new password",
        explanation: "This account has been asked to set a new password before signing in again.",
      };

    case LoginState.USER_MISSING_REQUIRED_PROPERTIES:
      return {
        kind: "continueElsewhere",
        title: "A few details first",
        explanation: "Your workspace asks for some details this screen does not collect yet.",
      };

    case LoginState.USER_MUST_BE_IN_AT_LEAST_ONE_ORG:
      return {
        kind: "continueElsewhere",
        title: "Join a workspace",
        explanation: "An account has to belong to a workspace before it can be used. You can join or create one.",
      };

    default:
      // Anything PropelAuth adds later. Deliberately not "signedIn": an
      // unknown state is not a session, and the safe direction is the one that
      // asks rather than the one that lets somebody in.
      return {
        kind: "continueElsewhere",
        title: "One more step",
        explanation: "Your account needs something this screen cannot do yet.",
      };
  }
}
